import styled from 'styled-components';

import { SectionHeading, SectionSub } from './section';

export { SectionHeading, SectionSub };

const MOBILE = '@media (max-width: 768px)';

export const StorySection = styled.section`
  max-width: 108rem;
  margin: 0 auto;
  padding: ${({ theme }) => `7.2rem ${theme.spacing.lg}`};

  ${MOBILE} {
    padding: ${({ theme }) => `4rem ${theme.spacing.lg}`};
  }
`;

export const StoryHeader = styled.div`
  text-align: center;
  margin-bottom: 0;
`;

export const StoryPanel = styled.div<{ $reverse?: boolean }>`
  display: flex;
  flex-direction: ${({ $reverse }) => ($reverse ? 'row-reverse' : 'row')};
  align-items: center;
  gap: 6.4rem;
  margin-bottom: 9.6rem;

  &:last-of-type {
    margin-bottom: 0;
  }

  > * {
    flex: 1;
  }

  ${MOBILE} {
    flex-direction: column-reverse;
    gap: 2.4rem;
    margin-bottom: 5.6rem;
  }
`;

export const StoryVisual = styled.div<{ $hasHint?: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ $hasHint }) => ($hasHint ? '7rem' : '2.4rem')} 0 2.4rem;

  ${MOBILE} {
    flex-wrap: wrap;
    gap: 0.6rem;
  }
`;

export const ScrollHint = styled.div`
  position: absolute;
  top: 0.4rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.2rem;
  width: max-content;
  max-width: min(34rem, calc(100vw - 4rem));
  padding: 0.7rem 1.2rem;
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1rem;
  box-shadow: 0 1rem 2.4rem rgba(15, 23, 42, 0.12);
  z-index: 3;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 0.7rem solid transparent;
    border-right: 0.7rem solid transparent;
  }
  &::before {
    top: 100%;
    border-top: 0.7rem solid ${({ theme }) => theme.colors.border};
  }
  &::after {
    top: calc(100% - 0.1rem);
    border-top: 0.7rem solid ${({ theme }) => theme.colors.white};
  }

  ${MOBILE} {
    padding: 0.6rem 1rem;
    margin-bottom: 1rem;
  }
`;

export const ScrollHintTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;

  & > span[aria-hidden] {
    display: inline-block;
    animation: scrollHintBob 1.8s ease-in-out infinite;
  }

  @keyframes scrollHintBob {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(0.3rem);
    }
  }

  ${MOBILE} {
    font-size: 1.15rem;
  }
`;

export const ScrollHintSub = styled.div`
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  line-height: 1.4;

  ${MOBILE} {
    font-size: 1rem;
  }
`;

export const StoryPhoneFrame = styled.div`
  position: relative;
  width: 33rem;
  aspect-ratio: 414 / 896;
  background-color: ${({ theme }) => theme.colors.darkSlate};
  border-radius: 4.4rem;
  padding: 1rem;
  box-shadow:
    0 4rem 8rem rgba(15, 23, 42, 0.25),
    0 0 0 0.1rem rgba(15, 23, 42, 0.1);
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    top: 1.4rem;
    left: 50%;
    transform: translateX(-50%);
    width: 10.4rem;
    height: 2.6rem;
    background-color: #000000;
    border-radius: 1.4rem;
    z-index: 5;
    pointer-events: none;
  }

  ${MOBILE} {
    width: 26rem;
    border-radius: 3.6rem;

    &::after {
      top: 1.1rem;
      width: 8.4rem;
      height: 2.2rem;
      border-radius: 1.2rem;
    }
  }
`;

export const StoryAnnotation = styled.div<{
  $top?: string;
  $left?: string;
  $right?: string;
  $bottom?: string;
}>`
  position: absolute;
  ${({ $top }) => ($top ? `top: ${$top};` : '')}
  ${({ $left }) => ($left ? `left: ${$left};` : '')}
  ${({ $right }) => ($right ? `right: ${$right};` : '')}
  ${({ $bottom }) => ($bottom ? `bottom: ${$bottom};` : '')}
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1.4rem;
  padding: 0.8rem 1.2rem;
  box-shadow: 0 1rem 2.4rem rgba(15, 23, 42, 0.2);
  z-index: 3;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkSlate};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  ${MOBILE} {
    position: static;
    font-size: 1.05rem;
    padding: 0.5rem 0.9rem;
  }
`;

export const StoryAnnotationAccent = styled(StoryAnnotation)`
  color: ${({ theme }) => theme.colors.successText};
  background-color: ${({ theme }) => theme.colors.successBgSoft};
  border-color: ${({ theme }) => theme.colors.successBorder};
`;

export const PanelHeader = styled.div`
  text-align: center;
  margin-bottom: 3.2rem;

  ${MOBILE} {
    margin-bottom: 2rem;
  }
`;

export const StoryText = styled.div``;

export const StoryActLabel = styled.span`
  display: inline-block;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.accent};
  background-color: ${({ theme }) => theme.colors.accentLight};
  border-radius: 999rem;
  padding: 0.4rem 1.2rem;
  text-transform: uppercase;
  letter-spacing: 0.15rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const StoryActTitle = styled.h3`
  font-size: 3rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  line-height: 1.2;
  letter-spacing: -0.05rem;

  ${MOBILE} {
    font-size: 2.4rem;
  }
`;

export const StoryActDesc = styled.p`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  line-height: 1.6;
`;

export const StoryActDescAccent = styled.span`
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 600;
`;








